// src/components/TutorViewToggle.jsx
import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const GREEN = "#097969";
const BLUE = "#0E4D92";

// Visible only on: Dashboard + Subject Middle + Subject Page
const isAllowedPath = (pathname) => {
  if (!pathname) return false;

  return (
    pathname === "/dashboard" ||
    pathname === "/dashboard-tutor" ||
    pathname.startsWith("/subject/") ||
    pathname.startsWith("/subject-tutor/")
  );
};

// Switch rules:
// - Middle pages: /subject/:slug  <->  /subject-tutor/:slug
// - Subject pages: /subject/:slug/learn...  <->  /subject/:slug/edit...
const mapPathToMode = (pathname, targetMode) => {
  if (!pathname) return pathname;

  // ----- Dashboard -----
  if (pathname === "/dashboard-tutor") {
    return targetMode === "student" ? "/dashboard" : "/dashboard-tutor";
  }
  if (pathname === "/dashboard") {
    return targetMode === "tutor" ? "/dashboard-tutor" : "/dashboard";
  }

  // ----- Subject PAGES (learn/edit at end) -----
  const learnMatch = pathname.match(/^\/subject\/([^/]+)\/learn(\/.*)?$/);
  if (learnMatch) {
    const slug = learnMatch[1];
    const rest = learnMatch[2] || "";
    return targetMode === "tutor"
      ? `/subject/${slug}/edit${rest}`
      : `/subject/${slug}/learn${rest}`;
  }

  const editMatch = pathname.match(/^\/subject\/([^/]+)\/edit(\/.*)?$/);
  if (editMatch) {
    const slug = editMatch[1];
    const rest = editMatch[2] || "";
    return targetMode === "student"
      ? `/subject/${slug}/learn${rest}`
      : `/subject/${slug}/edit${rest}`;
  }

  // ----- Middle pages -----
  const tutorMiddleMatch = pathname.match(/^\/subject-tutor\/([^/]+)\/?$/);
  if (tutorMiddleMatch) {
    const slug = tutorMiddleMatch[1];
    return targetMode === "student"
      ? `/subject/${slug}`
      : `/subject-tutor/${slug}`;
  }

  const studentMiddleMatch = pathname.match(/^\/subject\/([^/]+)\/?$/);
  if (studentMiddleMatch) {
    const slug = studentMiddleMatch[1];
    return targetMode === "tutor"
      ? `/subject-tutor/${slug}`
      : `/subject/${slug}`;
  }

  return pathname;
};

const EyeIcon = ({ color }) => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M2.25 12s3.75-7.5 9.75-7.5S21.75 12 21.75 12s-3.75 7.5-9.75 7.5S2.25 12 2.25 12Z"
      stroke={color}
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 15.25a3.25 3.25 0 1 0 0-6.5 3.25 3.25 0 0 0 0 6.5Z"
      stroke={color}
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TutorViewToggle = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // ✅ Hooks MUST be called before any early return
  const [isHover, setIsHover] = useState(false);

  const role = localStorage.getItem("userRole");
  const isTutor = role === "TUTOR" || role === "ADMIN";

  const storedMode = localStorage.getItem("tutorViewMode");
  const mode = storedMode === "student" ? "student" : "tutor";

  const shouldShow = isTutor && isAllowedPath(location.pathname);

  const baseColor = mode === "tutor" ? GREEN : BLUE;
  const hoverColor = mode === "tutor" ? BLUE : GREEN;

  const labelText = mode === "tutor" ? "Tutor Edit" : "Student View";

  const currentColor = useMemo(
    () => (isHover ? hoverColor : baseColor),
    [isHover, hoverColor, baseColor]
  );

  const onToggle = () => {
    const nextMode = mode === "tutor" ? "student" : "tutor";
    localStorage.setItem("tutorViewMode", nextMode);

    const nextPath = mapPathToMode(location.pathname, nextMode);
    if (nextPath && nextPath !== location.pathname) {
      navigate(nextPath);
    } else {
      navigate(location.pathname, { replace: true });
    }
  };

  // ✅ Early return AFTER hooks
  if (!shouldShow) return null;

  return (
    <div
      style={{
        position: "fixed",
        right: 24,
        bottom: 45,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        gap: 10,
        userSelect: "none",
      }}
    >
      {/* Rectangle */}
      <div
        style={{
          width: 98,
          height: 25,
          borderRadius: 15,
          background: currentColor,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: '"Public Sans", sans-serif',
          fontWeight: 700,
          fontSize: 13,
          color: "#ffffff",
          lineHeight: 1,
          transition: "background 160ms ease",
          cursor: "pointer",
        }}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") onToggle();
        }}
        aria-label="Ansicht umschalten"
      >
        {labelText}
      </div>

      {/* Eye */}
      <button
        type="button"
        onClick={onToggle}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
        aria-label="Ansicht umschalten"
        style={{
          width: 30,
          height: 30,
          padding: 0,
          border: "none",
          background: "transparent",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <EyeIcon color={currentColor} />
      </button>
    </div>
  );
};

export default TutorViewToggle;
